import { z } from "zod";

import {
  attachmentClassificationWarningSchema,
  attachmentDispositionSchema,
  attachmentSourceTypeSchema,
  classifyAttachmentSource,
  createAttachmentArchiveFilenames,
  normalizeAttachmentFilename,
} from "./attachments";
import {
  trelloBoardCaptureSchema,
  trelloIdSchema,
  type TrelloBoardCapture,
} from "./trello-capture";

const portableArchivePathSchema = z
  .string()
  .min(1)
  .max(1_024)
  .superRefine((path, context) => {
    const segments = path.split("/");

    if (
      path.startsWith("/") ||
      path.includes("\\") ||
      segments.some(
        (segment) => segment === "" || segment === "." || segment === "..",
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Portable paths must be normalized relative paths.",
      });
    }
  });

const portableCustomFieldValueSchema = z.discriminatedUnion("type", [
  z.object({ checked: z.boolean(), type: z.literal("checkbox") }),
  z.object({ date: z.iso.datetime(), type: z.literal("date") }),
  z.object({ number: z.string(), type: z.literal("number") }),
  z.object({ optionId: trelloIdSchema, type: z.literal("option") }),
  z.object({ text: z.string(), type: z.literal("text") }),
  z.object({ type: z.literal("unavailable") }),
]);

const portableCustomFieldItemSchema = z.object({
  customFieldId: trelloIdSchema,
  id: trelloIdSchema,
  value: portableCustomFieldValueSchema,
});

const portableBoardSchema = z.object({
  closed: z.boolean(),
  dateLastActivity: z.iso.datetime().nullable(),
  description: z.string(),
  id: trelloIdSchema,
  name: z.string().min(1),
  preferences: z.object({
    background: z.string().nullable(),
    backgroundBottomColor: z.string().nullable(),
    backgroundBrightness: z.string().nullable(),
    backgroundTile: z.boolean().nullable(),
    backgroundTopColor: z.string().nullable(),
    calendarFeedEnabled: z.boolean().nullable(),
    cardAging: z.string().nullable(),
    cardCovers: z.boolean(),
    comments: z.string().nullable(),
    hideVotes: z.boolean().nullable(),
    isTemplate: z.boolean().nullable(),
    permissionLevel: z.string().min(1),
    selfJoin: z.boolean().nullable(),
    voting: z.string().nullable(),
  }),
  shortLink: z.string().nullable(),
  shortUrl: z.string().nullable(),
  subscribed: z.boolean().nullable(),
  url: z.string().nullable(),
  workspaceId: trelloIdSchema.nullable(),
});

const portableMemberSchema = z.object({
  fullName: z.string().min(1),
  id: trelloIdSchema,
  username: z.string().min(1),
});

const portableBoardMembershipSchema = z.object({
  boardId: trelloIdSchema,
  deactivated: z.boolean().nullable(),
  id: trelloIdSchema,
  memberId: trelloIdSchema.nullable(),
  memberType: z.string().min(1).nullable(),
  unconfirmed: z.boolean().nullable(),
});

const portableListSchema = z.object({
  boardId: trelloIdSchema,
  closed: z.boolean(),
  id: trelloIdSchema,
  name: z.string().min(1),
  position: z.number().nonnegative(),
});

const portableLabelSchema = z.object({
  boardId: trelloIdSchema,
  color: z.string().min(1).nullable(),
  id: trelloIdSchema,
  name: z.string(),
});

const portableCardSchema = z.object({
  boardId: trelloIdSchema,
  checklistIds: z.array(trelloIdSchema),
  closed: z.boolean(),
  cover: z
    .object({
      attachmentId: trelloIdSchema.nullable(),
      brightness: z.string().min(1),
      color: z.string().min(1).nullable(),
      isTemplate: z.boolean().nullable(),
      providerData: z.record(z.string(), z.json()),
      size: z.string().min(1),
      uploadedBackgroundReference: z
        .union([trelloIdSchema, z.boolean()])
        .nullable(),
    })
    .nullable(),
  coverAttachmentId: trelloIdSchema.nullable(),
  customFieldItems: z.array(portableCustomFieldItemSchema),
  dateLastActivity: z.iso.datetime().nullable(),
  description: z.string(),
  due: z.iso.datetime().nullable(),
  dueComplete: z.boolean(),
  dueReminderMinutes: z.number().int().nullable(),
  id: trelloIdSchema,
  labelIds: z.array(trelloIdSchema),
  listId: trelloIdSchema,
  manualCoverAttachment: z.boolean().nullable(),
  memberIds: z.array(trelloIdSchema),
  name: z.string().min(1),
  number: z.number().int().nonnegative().nullable(),
  position: z.number().nonnegative(),
  shortLink: z.string().nullable(),
  shortUrl: z.string().nullable(),
  start: z.iso.datetime().nullable(),
  subscribed: z.boolean().nullable(),
  url: z.string().nullable(),
  voterMemberIds: z.array(trelloIdSchema),
});

const portableCheckItemSchema = z.object({
  due: z.iso.datetime().nullable(),
  id: trelloIdSchema,
  memberId: trelloIdSchema.nullable(),
  name: z.string().min(1),
  position: z.number().nonnegative(),
  state: z.enum(["complete", "incomplete"]),
});

const portableChecklistSchema = z.object({
  boardId: trelloIdSchema,
  cardId: trelloIdSchema,
  id: trelloIdSchema,
  items: z.array(portableCheckItemSchema),
  name: z.string().min(1),
  position: z.number().nonnegative(),
});

const portableCustomFieldOptionSchema = z.object({
  color: z.string().min(1),
  id: trelloIdSchema,
  position: z.number().nonnegative(),
  text: z.string(),
});

const portableCustomFieldSchema = z.object({
  boardId: trelloIdSchema,
  displayOnCardFront: z.boolean().nullable(),
  fieldGroup: z.string().min(1),
  id: trelloIdSchema,
  name: z.string().min(1),
  options: z.array(portableCustomFieldOptionSchema),
  position: z.number().nonnegative(),
  type: z.enum(["checkbox", "date", "list", "number", "text"]),
});

const portableCommentSchema = z.object({
  author: portableMemberSchema,
  boardId: trelloIdSchema,
  cardId: trelloIdSchema,
  cardName: z.string().nullable(),
  createdAt: z.iso.datetime(),
  id: trelloIdSchema,
  text: z.string(),
});

const portableActivitySchema = z.object({
  actor: portableMemberSchema,
  boardId: trelloIdSchema,
  cardId: trelloIdSchema,
  cardName: z.string().nullable(),
  createdAt: z.iso.datetime(),
  data: z.record(z.string(), z.json()),
  display: z.json().nullable(),
  id: trelloIdSchema,
  sources: z
    .array(z.enum(["trello_historical_read", "trello_webhook_carry_forward"]))
    .min(1)
    .max(2)
    .refine((sources) => new Set(sources).size === sources.length, {
      message: "Activity provenance sources must be unique.",
    })
    .optional(),
  type: z.string().min(1),
});

const portableActivityCoverageSchema = z.object({
  historicalRead: z.literal("trello_board_actions"),
  limitations: z.array(
    z.enum([
      "webhook_coverage_starts_after_activation",
      "webhook_delivery_is_best_effort",
      "original_actors_and_timestamps_cannot_be_replayed",
    ]),
  ),
  webhookCarryForward: z.object({
    activatedAt: z.iso.datetime(),
    assembledAt: z.iso.datetime(),
    carriedForwardActionCount: z.int().nonnegative(),
    carriedForwardFromSnapshotId: z.uuid().nullable(),
    duplicateActionCount: z.int().nonnegative(),
    eligibleStoredActionCount: z.int().nonnegative(),
    mergedActionCount: z.int().nonnegative(),
    snapshotCutoffAt: z.iso.datetime(),
    unsupportedActionCount: z.int().nonnegative(),
  }),
});

export const portableAttachmentSchema = z.object({
  attachedAt: z.iso.datetime().nullable(),
  byteSize: z.int().nonnegative().nullable(),
  cardId: trelloIdSchema,
  classificationWarningCode: attachmentClassificationWarningSchema.nullable(),
  disposition: attachmentDispositionSchema,
  id: trelloIdSchema,
  isUpload: z.boolean(),
  mimeType: z.string().nullable(),
  name: z.string(),
  plannedArchivePath: portableArchivePathSchema.nullable(),
  sourceFileName: z.string().min(1).nullable(),
  sourceType: attachmentSourceTypeSchema,
  url: z.string().nullable(),
});
export type PortableAttachment = z.infer<typeof portableAttachmentSchema>;

function duplicateIndexes(ids: readonly string[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];

  ids.forEach((id, index) => {
    if (seen.has(id)) {
      duplicates.push(index);
    }
    seen.add(id);
  });

  return duplicates;
}

export const portableBoardSnapshotV1Schema = z
  .object({
    activities: z.array(portableActivitySchema),
    activityCoverage: portableActivityCoverageSchema.optional(),
    attachments: z.array(portableAttachmentSchema),
    board: portableBoardSchema,
    capturedAt: z.iso.datetime(),
    cards: z.array(portableCardSchema),
    checklists: z.array(portableChecklistSchema),
    comments: z.array(portableCommentSchema),
    customFields: z.array(portableCustomFieldSchema),
    labels: z.array(portableLabelSchema),
    lists: z.array(portableListSchema),
    members: z.array(portableMemberSchema),
    memberships: z.array(portableBoardMembershipSchema),
    product: z.literal("board-preserve"),
    provider: z.literal("trello"),
    schemaVersion: z.literal(1),
    snapshotId: z.uuid(),
    source: z.object({
      boardId: trelloIdSchema,
      workspaceId: trelloIdSchema.nullable(),
    }),
  })
  .superRefine((snapshot, context) => {
    const collections = [
      ["activities", snapshot.activities],
      ["attachments", snapshot.attachments],
      ["cards", snapshot.cards],
      ["checklists", snapshot.checklists],
      ["comments", snapshot.comments],
      ["customFields", snapshot.customFields],
      ["labels", snapshot.labels],
      ["lists", snapshot.lists],
      ["members", snapshot.members],
      ["memberships", snapshot.memberships],
    ] as const;

    for (const [collectionName, collection] of collections) {
      for (const index of duplicateIndexes(collection.map(({ id }) => id))) {
        context.addIssue({
          code: "custom",
          message: `${collectionName} IDs must be unique.`,
          path: [collectionName, index, "id"],
        });
      }
    }

    if (
      snapshot.board.id !== snapshot.source.boardId ||
      snapshot.board.workspaceId !== snapshot.source.workspaceId
    ) {
      context.addIssue({
        code: "custom",
        message: "Portable source identifiers must match the board.",
        path: ["source"],
      });
    }

    const boardId = snapshot.board.id;
    const cardIds = new Set(snapshot.cards.map(({ id }) => id));
    const cardById = new Map(snapshot.cards.map((card) => [card.id, card]));
    const checklistById = new Map(
      snapshot.checklists.map((checklist) => [checklist.id, checklist]),
    );
    const customFieldById = new Map(
      snapshot.customFields.map((field) => [field.id, field]),
    );
    const labelIds = new Set(snapshot.labels.map(({ id }) => id));
    const listIds = new Set(snapshot.lists.map(({ id }) => id));
    const memberIds = new Set(snapshot.members.map(({ id }) => id));
    const seenCheckItemIds = new Set<string>();
    const seenCustomFieldItemIds = new Set<string>();
    const seenCustomFieldOptionIds = new Set<string>();

    snapshot.lists.forEach((list, index) => {
      if (list.boardId !== boardId) {
        context.addIssue({
          code: "custom",
          message: "List must reference the captured board.",
          path: ["lists", index, "boardId"],
        });
      }
    });

    snapshot.labels.forEach((label, index) => {
      if (label.boardId !== boardId) {
        context.addIssue({
          code: "custom",
          message: "Label must reference the captured board.",
          path: ["labels", index, "boardId"],
        });
      }
    });

    snapshot.cards.forEach((card, index) => {
      const referencesCapturedBoard = card.boardId === boardId;
      const hasList = listIds.has(card.listId);
      const hasMembers = card.memberIds.every((id) => memberIds.has(id));
      const hasLabels = card.labelIds.every((id) => labelIds.has(id));
      const hasChecklists = card.checklistIds.every(
        (id) => checklistById.get(id)?.cardId === card.id,
      );
      const hasCustomFields = card.customFieldItems.every((item) => {
        const field = customFieldById.get(item.customFieldId);
        if (field === undefined) {
          return false;
        }

        if (item.value.type === "unavailable") {
          return true;
        }

        if (item.value.type === "option") {
          const optionId = item.value.optionId;
          return (
            field.type === "list" &&
            field.options.some(({ id }) => id === optionId)
          );
        }

        return field.type === item.value.type;
      });

      if (
        card.cover !== null &&
        card.cover.attachmentId !== null &&
        card.coverAttachmentId !== null &&
        card.cover.attachmentId !== card.coverAttachmentId
      ) {
        context.addIssue({
          code: "custom",
          message: "Card cover attachment references must match.",
          path: ["cards", index, "cover", "attachmentId"],
        });
      }

      if (
        !referencesCapturedBoard ||
        !hasList ||
        !hasMembers ||
        !hasLabels ||
        !hasChecklists ||
        !hasCustomFields
      ) {
        context.addIssue({
          code: "custom",
          message: "Card contains a reference outside the captured board.",
          path: ["cards", index],
        });
      }

      card.customFieldItems.forEach((item, itemIndex) => {
        if (seenCustomFieldItemIds.has(item.id)) {
          context.addIssue({
            code: "custom",
            message: "Custom-field item IDs must be unique.",
            path: ["cards", index, "customFieldItems", itemIndex, "id"],
          });
        }
        seenCustomFieldItemIds.add(item.id);
      });
    });

    snapshot.checklists.forEach((checklist, index) => {
      const card = cardById.get(checklist.cardId);
      const hasUnknownMember = checklist.items.some(
        ({ memberId }) => memberId !== null && !memberIds.has(memberId),
      );
      if (
        checklist.boardId !== boardId ||
        card === undefined ||
        !card.checklistIds.includes(checklist.id) ||
        hasUnknownMember
      ) {
        context.addIssue({
          code: "custom",
          message: "Checklist contains a reference outside the captured board.",
          path: ["checklists", index],
        });
      }

      checklist.items.forEach((item, itemIndex) => {
        if (seenCheckItemIds.has(item.id)) {
          context.addIssue({
            code: "custom",
            message: "Check-item IDs must be unique.",
            path: ["checklists", index, "items", itemIndex, "id"],
          });
        }
        seenCheckItemIds.add(item.id);
      });
    });

    snapshot.customFields.forEach((field, fieldIndex) => {
      if (field.boardId !== boardId) {
        context.addIssue({
          code: "custom",
          message: "Custom field must reference the captured board.",
          path: ["customFields", fieldIndex, "boardId"],
        });
      }

      if (field.type !== "list" && field.options.length > 0) {
        context.addIssue({
          code: "custom",
          message: "Only list custom fields may define options.",
          path: ["customFields", fieldIndex, "options"],
        });
      }

      field.options.forEach((option, optionIndex) => {
        if (seenCustomFieldOptionIds.has(option.id)) {
          context.addIssue({
            code: "custom",
            message: "Custom-field option IDs must be unique.",
            path: ["customFields", fieldIndex, "options", optionIndex, "id"],
          });
        }
        seenCustomFieldOptionIds.add(option.id);
      });
    });

    snapshot.comments.forEach((comment, index) => {
      if (comment.boardId !== boardId) {
        context.addIssue({
          code: "custom",
          message: "Comment must reference the captured board.",
          path: ["comments", index],
        });
      }
    });

    snapshot.activities.forEach((activity, index) => {
      if (activity.boardId !== boardId) {
        context.addIssue({
          code: "custom",
          message: "Activity must reference the captured board.",
          path: ["activities", index],
        });
      }
    });

    snapshot.memberships.forEach((membership, index) => {
      if (membership.boardId !== boardId) {
        context.addIssue({
          code: "custom",
          message: "Membership must reference the captured board.",
          path: ["memberships", index],
        });
      }
    });

    snapshot.attachments.forEach((attachment, index) => {
      const shouldHavePath = attachment.disposition === "copy_candidate";
      const hasCardScopedPath =
        attachment.plannedArchivePath === null ||
        (attachment.plannedArchivePath.startsWith("attachments/") &&
          attachment.plannedArchivePath.includes(`--${attachment.cardId}/`));
      if (
        !cardIds.has(attachment.cardId) ||
        shouldHavePath !== (attachment.plannedArchivePath !== null) ||
        !hasCardScopedPath
      ) {
        context.addIssue({
          code: "custom",
          message:
            "Attachment must reference a captured card and only copy candidates may have a planned path.",
          path: ["attachments", index],
        });
      }
    });
  });

export type PortableBoardSnapshotV1 = z.infer<
  typeof portableBoardSnapshotV1Schema
>;

export interface NormalizeTrelloCaptureOptions {
  capturedAt: string;
  snapshotId: string;
}

function compareIds(left: { id: string }, right: { id: string }): number {
  return left.id.localeCompare(right.id);
}

function comparePositioned(
  left: { id: string; pos: number },
  right: { id: string; pos: number },
): number {
  return left.pos - right.pos || compareIds(left, right);
}

function normalizeCustomFieldValue(
  item: TrelloBoardCapture["cards"][number]["customFieldItems"][number],
): z.infer<typeof portableCustomFieldValueSchema> {
  if (item.value !== undefined) {
    if ("checked" in item.value) {
      return { checked: item.value.checked === "true", type: "checkbox" };
    }
    if ("date" in item.value) {
      return { date: item.value.date, type: "date" };
    }
    if ("number" in item.value) {
      return { number: item.value.number, type: "number" };
    }
    return { text: item.value.text, type: "text" };
  }

  if (item.idValue !== undefined) {
    return { optionId: item.idValue, type: "option" };
  }

  return { type: "unavailable" };
}

export function normalizeTrelloCapture(
  captureInput: TrelloBoardCapture,
  options: NormalizeTrelloCaptureOptions,
): PortableBoardSnapshotV1 {
  const capture = trelloBoardCaptureSchema.parse(captureInput);
  const metadata = z
    .object({ capturedAt: z.iso.datetime(), snapshotId: z.uuid() })
    .parse(options);
  const lists = capture.lists.toSorted(comparePositioned);
  const listOrder = new Map(lists.map(({ id }, index) => [id, index]));
  const cards = capture.cards.toSorted(
    (left, right) =>
      (listOrder.get(left.idList) ?? Number.MAX_SAFE_INTEGER) -
        (listOrder.get(right.idList) ?? Number.MAX_SAFE_INTEGER) ||
      comparePositioned(left, right),
  );
  const cardOrder = new Map(cards.map(({ id }, index) => [id, index]));
  const cardById = new Map(cards.map((card) => [card.id, card]));
  const activities = capture.actions
    .filter((action) => action.data.card !== undefined)
    .toSorted(
      (left, right) =>
        left.date.localeCompare(right.date) || compareIds(left, right),
    );
  const copyCandidates = cards.flatMap((card) =>
    card.attachments
      .filter(
        (attachment) =>
          classifyAttachmentSource({
            isUpload: attachment.isUpload,
            url: attachment.url,
          }).disposition === "copy_candidate",
      )
      .map((attachment) => ({
        attachmentId: attachment.id,
        name: attachment.name,
      })),
  );
  const archiveFilenames = createAttachmentArchiveFilenames(copyCandidates);

  return portableBoardSnapshotV1Schema.parse({
    activities: activities.map((action) => ({
      actor: action.memberCreator,
      boardId: capture.board.id,
      cardId: action.data.card!.id,
      cardName:
        action.data.card!.name ??
        cardById.get(action.data.card!.id)?.name ??
        null,
      createdAt: action.date,
      data: action.data,
      display: action.display ?? null,
      id: action.id,
      type: action.type,
    })),
    attachments: cards.flatMap((card) =>
      card.attachments.toSorted(compareIds).map((attachment) => {
        const classification = classifyAttachmentSource({
          isUpload: attachment.isUpload,
          url: attachment.url,
        });
        const archiveFilename = archiveFilenames.get(attachment.id);

        return {
          attachedAt: attachment.date ?? null,
          byteSize: attachment.bytes ?? null,
          cardId: card.id,
          classificationWarningCode: classification.warningCode,
          disposition: classification.disposition,
          id: attachment.id,
          isUpload: attachment.isUpload,
          mimeType: attachment.mimeType ?? null,
          name: attachment.name,
          plannedArchivePath:
            classification.disposition === "copy_candidate" &&
            archiveFilename !== undefined
              ? `attachments/${normalizeAttachmentFilename(card.name)}--${card.id}/${archiveFilename}`
              : null,
          sourceFileName: attachment.fileName ?? null,
          sourceType: classification.sourceType,
          url: attachment.url,
        };
      }),
    ),
    board: {
      closed: capture.board.closed,
      dateLastActivity: capture.board.dateLastActivity ?? null,
      description: capture.board.desc,
      id: capture.board.id,
      name: capture.board.name,
      preferences: {
        background: capture.board.prefs.background ?? null,
        backgroundBottomColor:
          capture.board.prefs.backgroundBottomColor ?? null,
        backgroundBrightness: capture.board.prefs.backgroundBrightness ?? null,
        backgroundTile: capture.board.prefs.backgroundTile ?? null,
        backgroundTopColor: capture.board.prefs.backgroundTopColor ?? null,
        calendarFeedEnabled: capture.board.prefs.calendarFeedEnabled ?? null,
        cardAging: capture.board.prefs.cardAging ?? null,
        cardCovers: capture.board.prefs.cardCovers,
        comments: capture.board.prefs.comments ?? null,
        hideVotes: capture.board.prefs.hideVotes ?? null,
        isTemplate: capture.board.prefs.isTemplate ?? null,
        permissionLevel: capture.board.prefs.permissionLevel,
        selfJoin: capture.board.prefs.selfJoin ?? null,
        voting: capture.board.prefs.voting ?? null,
      },
      shortLink: capture.board.shortLink ?? null,
      shortUrl: capture.board.shortUrl ?? null,
      subscribed: capture.board.subscribed ?? null,
      url: capture.board.url ?? null,
      workspaceId: capture.board.idOrganization,
    },
    capturedAt: metadata.capturedAt,
    cards: cards.map((card) => ({
      boardId: card.idBoard,
      checklistIds: [...card.idChecklists],
      closed: card.closed,
      cover:
        card.cover === undefined
          ? null
          : {
              attachmentId:
                card.cover.idAttachment ?? card.idAttachmentCover ?? null,
              brightness: card.cover.brightness,
              color: card.cover.color,
              isTemplate: card.cover.isTemplate ?? null,
              providerData: card.cover,
              size: card.cover.size,
              uploadedBackgroundReference:
                card.cover.idUploadedBackground ?? null,
            },
      coverAttachmentId:
        card.idAttachmentCover ?? card.cover?.idAttachment ?? null,
      customFieldItems: card.customFieldItems
        .toSorted(
          (left, right) =>
            left.idCustomField.localeCompare(right.idCustomField) ||
            compareIds(left, right),
        )
        .map((item) => ({
          customFieldId: item.idCustomField,
          id: item.id,
          value: normalizeCustomFieldValue(item),
        })),
      description: card.desc,
      dateLastActivity: card.dateLastActivity ?? null,
      due: card.due,
      dueComplete: card.dueComplete,
      dueReminderMinutes: card.dueReminder ?? null,
      id: card.id,
      labelIds: [...card.idLabels],
      listId: card.idList,
      manualCoverAttachment: card.manualCoverAttachment ?? null,
      memberIds: [...card.idMembers],
      name: card.name,
      number: card.idShort ?? null,
      position: card.pos,
      shortLink: card.shortLink ?? null,
      shortUrl: card.shortUrl ?? null,
      start: card.start,
      subscribed: card.subscribed ?? null,
      url: card.url ?? null,
      voterMemberIds: [...card.idMembersVoted],
    })),
    checklists: capture.checklists
      .toSorted(
        (left, right) =>
          (cardOrder.get(left.idCard) ?? Number.MAX_SAFE_INTEGER) -
            (cardOrder.get(right.idCard) ?? Number.MAX_SAFE_INTEGER) ||
          comparePositioned(left, right),
      )
      .map((checklist) => ({
        boardId: checklist.idBoard,
        cardId: checklist.idCard,
        id: checklist.id,
        items: checklist.checkItems.toSorted(comparePositioned).map((item) => ({
          due: item.due,
          id: item.id,
          memberId: item.idMember,
          name: item.name,
          position: item.pos,
          state: item.state,
        })),
        name: checklist.name,
        position: checklist.pos,
      })),
    comments: activities
      .filter(
        (action) =>
          action.type === "commentCard" && typeof action.data.text === "string",
      )
      .map((action) => ({
        author: action.memberCreator,
        boardId: capture.board.id,
        cardId: action.data.card!.id,
        cardName:
          action.data.card!.name ??
          cardById.get(action.data.card!.id)?.name ??
          null,
        createdAt: action.date,
        id: action.id,
        text: action.data.text!,
      })),
    customFields: capture.customFields
      .toSorted(comparePositioned)
      .map((field) => ({
        boardId: field.idModel,
        displayOnCardFront: field.display?.cardFront ?? null,
        fieldGroup: field.fieldGroup,
        id: field.id,
        name: field.name,
        options: (field.options ?? [])
          .toSorted(comparePositioned)
          .map((option) => ({
            color: option.color,
            id: option.id,
            position: option.pos,
            text: option.value.text,
          })),
        position: field.pos,
        type: field.type,
      })),
    labels: capture.labels.toSorted(compareIds).map((label) => ({
      boardId: label.idBoard,
      color: label.color,
      id: label.id,
      name: label.name,
    })),
    lists: lists.map((list) => ({
      boardId: list.idBoard,
      closed: list.closed,
      id: list.id,
      name: list.name,
      position: list.pos,
    })),
    members: capture.members.toSorted(compareIds),
    memberships: capture.memberships.toSorted(compareIds).map((membership) => ({
      boardId: capture.board.id,
      deactivated: membership.deactivated ?? null,
      id: membership.id,
      memberId: membership.idMember ?? null,
      memberType: membership.memberType ?? null,
      unconfirmed: membership.unconfirmed ?? null,
    })),
    product: "board-preserve",
    provider: "trello",
    schemaVersion: 1,
    snapshotId: metadata.snapshotId,
    source: {
      boardId: capture.board.id,
      workspaceId: capture.board.idOrganization,
    },
  });
}
